import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useLocation, useNavigate, useNavigationType, Navigate } from 'react-router-dom';
import { hubAPI } from '../services/hubApi';
import { wordpressAPI } from '../services/wordpressApi';
import { HubWithContent, HubSection } from '../types/hub';
import { WPPost, WPMedia } from '../types/wordpress';
import LoadingSkeleton from '../components/ui/LoadingSkeleton';
import ErrorState from '../components/ui/ErrorState';
import ResourceCard from '../components/resources/ResourceCard';
import ResourceCardSkeleton from '../components/resources/ResourceCardSkeleton';
import Breadcrumb from '../components/layout/Breadcrumb';
import { PAGE_SIDEBAR_LAYOUT, PageSidebar } from '../components/layout/OnThisPage';
import { savedHistoryScroll } from '../components/layout/Layout';
import { ArrowLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Input from '../components/ui/Input';
import { decodeHtmlEntities } from '../utils/htmlEntities';
import DOMPurify from 'dompurify';
import { filterDocumentAttachments } from '../utils/resourceAttachments';
import { hubProseClassName } from '../utils/hubProse';
import { pageFromTo, readPageFrom, reopenSearchState } from '../utils/pageFrom';
import { usePageTitle } from '../hooks/usePageTitle';
import { useScrollSpy } from '../hooks/useScrollSpy';
import SaveToMyOrbconButton from '../components/saved/SaveToMyOrbconButton';
import InThisNav from '../components/programs/InThisNav';
import ToolkitSectionTree, { toolkitNavItems } from '../components/hubs/ToolkitSectionTree';
import { programHubHref, hubOutline, orderedByOrder, pinScrollAnchor, hashScrollBehavior, withSmoothScroll, hubOutlineTree } from '../utils/hubChapter';
import { sectionAnchors } from '../utils/headingToc';
import { subjectPath } from '../utils/placePaths';
import { closePlace, placeEnterClass, PlaceRoom, readPlaceSurface, usePlacePage, usePlaceSlide } from '../utils/placeMotion';

type HubViewCache = {
  hub: HubWithContent;
  posts: Map<number, WPPost>;
  attachments: Map<number, WPMedia[]>;
};

const hubViewCache = new Map<string, HubViewCache>();

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

export default function HubDetail() {
  const { slug: routeSlug, childSlug, subjectSlug } = useParams<{
    slug?: string;
    childSlug?: string;
    subjectSlug?: string;
  }>();
  const slug = routeSlug ?? childSlug;
  const location = useLocation();
  const navigate = useNavigate();
  const panelFrom = readPageFrom(location.state);
  const openedSurface = readPlaceSurface(location.state);
  const pageRef = useRef<HTMLDivElement>(null);
  usePlacePage(pageRef);
  const seeded = slug ? hubViewCache.get(slug) : undefined;
  const [hub, setHub] = useState<HubWithContent | null>(seeded?.hub ?? null);
  const [resourcePosts, setResourcePosts] = useState<Map<number, WPPost>>(
    () => seeded?.posts ?? new Map()
  );
  const [attachmentsByPostId, setAttachmentsByPostId] = useState<Map<number, WPMedia[]>>(
    () => seeded?.attachments ?? new Map()
  );
  const [loading, setLoading] = useState(!seeded);
  const [postsReady, setPostsReady] = useState(() => {
    if (!seeded) return false;
    return seeded.hub.resources.every((resource) => seeded.posts.has(resource.wpPostId));
  });
  const slide = usePlaceSlide();
  const [error, setError] = useState<Error | null>(null);
  const [missingCount, setMissingCount] = useState(0);
  const [resourceQuery, setResourceQuery] = useState('');
  usePageTitle(hub?.title);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!slug) {
        setError(new Error('Invalid toolkit slug'));
        setLoading(false);
        setPostsReady(true);
        return;
      }

      const cached = hubViewCache.get(slug);
      try {
        setError(null);
        if (!cached) {
          setLoading(true);
          setPostsReady(false);
        }

        const hubData = await hubAPI.fetchHubWithContent(slug);
        if (!hubData) {
          setError(new Error('Toolkit not found'));
          setLoading(false);
          setPostsReady(true);
          return;
        }
        setHub(hubData);
        setLoading(false);
        setMissingCount(0);
        if (!cached) {
          setResourcePosts(new Map());
          setResourceQuery('');
        }

        if (hubData.resources.length === 0) {
          setPostsReady(true);
          return;
        }

        const cachedPosts = cached?.posts;
        const cacheComplete = cachedPosts
          && hubData.resources.every((resource) => cachedPosts.has(resource.wpPostId));
        if (cacheComplete && cachedPosts) {
          setResourcePosts(new Map(cachedPosts));
          setPostsReady(true);
          return;
        }

        setPostsReady(false);
        const postsMap = new Map<number, WPPost>(cachedPosts ? [...cachedPosts] : []);
        let missing = 0;
        for (let index = 0; index < hubData.resources.length; index += 8) {
          const slice = hubData.resources.slice(index, index + 8);
          await Promise.all(
            slice.map(async (resource) => {
              if (postsMap.has(resource.wpPostId)) return;
              try {
                const post = await wordpressAPI.fetchPost(resource.wpPostId);
                postsMap.set(resource.wpPostId, post);
              } catch (err) {
                missing += 1;
                console.warn(`Failed to fetch resource ${resource.wpPostId}:`, err);
              }
            })
          );
          if (cancelled) return;
          setResourcePosts(new Map(postsMap));
          setMissingCount(missing);
        }
        if (!cancelled) setPostsReady(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load toolkit'));
        setLoading(false);
        setPostsReady(true);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!hub || hub.slug !== slug) return;
    hubViewCache.set(hub.slug, {
      hub,
      posts: resourcePosts,
      attachments: attachmentsByPostId,
    });
  }, [hub, resourcePosts, attachmentsByPostId, slug]);

  useEffect(() => {
    if (!hub?.resources.length) {
      setAttachmentsByPostId(new Map());
      return;
    }

    let cancelled = false;

    const loadAttachments = async () => {
      const map = new Map<number, WPMedia[]>();
      for (let i = 0; i < hub.resources.length; i += 8) {
        if (cancelled) return;
        const slice = hub.resources.slice(i, i + 8);
        const entries = await Promise.all(
          slice.map(async (resource) => {
            try {
              const media = await wordpressAPI.fetchAttachments(resource.wpPostId);
              return [resource.wpPostId, filterDocumentAttachments(media)] as const;
            } catch {
              return [resource.wpPostId, []] as const;
            }
          })
        );
        if (cancelled) return;
        for (const [postId, attachments] of entries) {
          map.set(postId, [...attachments]);
        }
        setAttachmentsByPostId(new Map(map));
      }
    };

    loadAttachments();
    return () => {
      cancelled = true;
    };
  }, [hub]);

  const normalizedQuery = resourceQuery.trim().toLowerCase();
  const anchors = useMemo(() => {
    if (!hub) return new Map<string, string>();
    return sectionAnchors([
      ...[...(hub.sections || [])]
        .sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))
        .map((section) => ({ id: section.id, title: section.title })),
      { id: 'more', title: 'More resources' },
    ]);
  }, [hub]);
  const sectionIds = useMemo(() => {
    if (!hub) return [];
    const entries = hubOutline(hub.sections || [], hub.resources || []);
    const tree = hubOutlineTree(entries);
    const walk = (nodes: typeof tree): string[] =>
      nodes.flatMap((node) => {
        const id = anchors.get(node.id) ?? node.id;
        return [id, ...walk(node.children)];
      });
    return walk(tree);
  }, [hub, anchors]);
  const activeSectionId = useScrollSpy(sectionIds);
  const navigationType = useNavigationType();

  useEffect(() => {
    const id = location.hash.replace(/^#/, '');
    if (!id || !hub) return;
    const saved = savedHistoryScroll(location.key);
    const historyRestore = navigationType === 'POP' && (
      (saved != null && saved > 0)
      || Boolean(location.state && typeof location.state === 'object' && (location.state as { restoreScroll?: boolean }).restoreScroll)
    );
    if (historyRestore) return;
    return pinScrollAnchor(id, hashScrollBehavior(location.state));
  }, [location.hash, location.key, location.state, navigationType, hub]);

  if (loading) {
    return (
      <div ref={pageRef} data-place-page className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white">
        <LoadingSkeleton lines={10} className="mb-4" />
        <LoadingSkeleton lines={5} />
      </div>
    );
  }

  if (error || !hub) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-white">
        <ErrorState
          error={error || new Error('Toolkit not found')}
          title="Toolkit not found"
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  if (hub.program && (!subjectSlug || subjectSlug !== hub.program.slug)) {
    return (
      <Navigate
        to={programHubHref(hub.program.slug, hub.slug)}
        replace
        state={location.state}
      />
    );
  }

  const sections: HubSection[] = [...(hub.sections || [])].sort(
    (a, b) => a.order - b.order
  );
  const hasContentSections = sections.some((s) => s.type === 'CONTENT');
  const outline = hubOutline(sections, hub.resources || []);
  const flatResources = orderedByOrder(hub.resources || []);
  const postMatches = (post: WPPost) => {
    if (!normalizedQuery) return true;
    return decodeHtmlEntities(post.title.rendered).toLowerCase().includes(normalizedQuery);
  };
  const visibleResources = (resources: typeof flatResources) =>
    resources.filter((resource) => {
      if (!normalizedQuery) return true;
      const post = resourcePosts.get(resource.wpPostId);
      return !!post && postMatches(post);
    });
  const visibleOutline = outline
    .map((entry) => ({ ...entry, resources: visibleResources(entry.resources) }))
    .filter((entry) => !normalizedQuery || entry.resources.length > 0);
  const visibleFlat = visibleResources(flatResources);
  const showResourceFilter = flatResources.length > 12;
  const matchCount = outline.length > 0
    ? visibleOutline.reduce((count, entry) => count + entry.resources.length, 0)
    : visibleFlat.length;

  // Avoid duplicating the short description when legacy WP body is the same text
  const wpBodyHtml = hub.wpContent?.content || '';
  const wpBodyText = stripHtml(wpBodyHtml);
  const descriptionText = (hub.description || '').trim();
  const showLegacyWpBody =
    !hasContentSections &&
    !!wpBodyHtml &&
    wpBodyText.length > 0 &&
    wpBodyText.toLowerCase() !== descriptionText.toLowerCase();
  const hubPath = hub.program ? programHubHref(hub.program.slug, hub.slug) : `/toolkits/${hub.slug}`;
  const programBack = hub.program
    ? { to: subjectPath(hub.program.slug), label: `Back to ${hub.program.title}` }
    : null;
  // Search keeps its own return; otherwise step up to the parent program when there is one.
  const fromSearch = Boolean(panelFrom?.reopenSearch || panelFrom?.pathname === '/search');
  const backTo = fromSearch && panelFrom
    ? pageFromTo(panelFrom)
    : programBack?.to ?? (panelFrom ? pageFromTo(panelFrom) : '/resources');
  const backLabel = fromSearch && panelFrom
    ? panelFrom.label
    : programBack?.label ?? panelFrom?.label ?? 'Back to Resources';
  const room: PlaceRoom = {
    title: hub.title,
    to: hubPath,
    backTo: hubPath,
    backLabel: `Back to ${hub.title}`,
    crumbs: [
      { label: 'Home', to: '/', step: 'leave' },
      { label: 'Resources', to: '/resources', step: 'leave' },
      ...(hub.program
        ? [{ label: hub.program.title, to: subjectPath(hub.program.slug), step: 'shallower' as const }]
        : []),
      { label: hub.title, to: hubPath, step: 'shallower' as const },
    ],
  };

  return (
    <div ref={pageRef} data-place-page className="bg-white">
      <div className="place-room">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <Breadcrumb
            items={[
              { label: 'Home', to: '/', step: 'leave' },
              { label: 'Resources', to: '/resources', step: 'leave' },
              ...(hub.program
                ? [{ label: hub.program.title, to: subjectPath(hub.program.slug), step: 'shallower' as const }]
                : []),
              { label: hub.title },
            ]}
          />
          <header id="hub-heading" className="flex items-start justify-between gap-4 pb-8">
            <h1 tabIndex={-1} className="text-3xl md:text-4xl font-bold text-gray-900 outline-none">
              {hub.title}
            </h1>
            <SaveToMyOrbconButton
              type="HUB"
              hubId={hub.id}
              title={hub.title}
              slug={hub.slug}
              variant="detail"
            />
          </header>
        </div>
      </div>
      <div className={`place-content max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 ${placeEnterClass(slide)}`}>
        <Link
          to={backTo}
          onClick={(event) => {
            closePlace(
              event,
              backTo,
              navigate,
              openedSurface,
              fromSearch ? reopenSearchState(panelFrom) : undefined
            );
          }}
          className="inline-flex items-center gap-2 text-primary hover:text-primary-dark mb-8 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>{backLabel}</span>
        </Link>

        <div className={outline.length > 0 ? PAGE_SIDEBAR_LAYOUT : undefined}>
          {outline.length > 0 ? (
            <PageSidebar>
              <InThisNav
                label="In this toolkit"
                items={toolkitNavItems(
                  hubOutlineTree(outline),
                  (node) => anchors.get(node.id) ?? node.id,
                  activeSectionId,
                  false
                )}
                onSelect={(id) => {
                  navigate({ hash: id }, { preventScrollReset: true, state: withSmoothScroll(location.state) });
                }}
              />
            </PageSidebar>
          ) : null}

          <div className="min-w-0">
              <div className="mb-10 pb-8 border-b border-gray-200">
                {hub.description && (
                  <p className="text-lg text-gray-600 max-w-3xl">{hub.description}</p>
                )}

                {!hasContentSections && hub.wpContent?.featuredMediaUrl && (
                  <div className="mt-6">
                    <img
                      src={hub.wpContent.featuredMediaUrl}
                      alt=""
                      className="w-full max-h-80 object-cover rounded-lg"
                    />
                  </div>
                )}

                {showLegacyWpBody && (
                  <div
                    className={`mt-6 ${hubProseClassName}`}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(wpBodyHtml, {
                        ADD_ATTR: ['target'],
                      }),
                    }}
                  />
                )}
              </div>

              <div className="min-w-0">
            {missingCount > 0 && (
              <p className="text-sm text-gray-600 mb-4">
                {missingCount === 1
                  ? '1 document could not be loaded.'
                  : `${missingCount} documents could not be loaded.`}
              </p>
            )}
            {showResourceFilter && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-8">
                <div className="relative max-w-xl flex-1">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <Input
                    type="search"
                    placeholder="Filter resources by title"
                    value={resourceQuery}
                    onChange={(event) => setResourceQuery(event.target.value)}
                    className="pl-10"
                    aria-label="Filter resources by title"
                  />
                </div>
                {normalizedQuery && (
                  <p className="text-sm text-gray-600" aria-live="polite">
                    {matchCount} {matchCount === 1 ? 'match' : 'matches'}
                  </p>
                )}
              </div>
            )}
            {outline.length > 0 ? (
              <>
                <ToolkitSectionTree
                  nodes={hubOutlineTree(visibleOutline)}
                  filtering={Boolean(normalizedQuery)}
                  idFor={(node) => anchors.get(node.id) ?? node.id}
                  renderEntry={(node) => {
                    const entry = node.entry;
                    if (!entry) return null;
                    const hasChildren = node.children.length > 0;
                    return (
                      <>
                        {entry.section?.type === 'CONTENT' && entry.section.body ? (
                          <div
                            className={`mb-3 ${hubProseClassName} [&>*:first-child]:mt-0 [&>*:last-child]:mb-0`}
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(entry.section.body, { ADD_ATTR: ['target'] }),
                            }}
                          />
                        ) : null}
                        {entry.section?.type === 'RESOURCE_GROUP' && entry.section.description ? (
                          <p className="text-gray-600 mb-5 max-w-3xl">{entry.section.description}</p>
                        ) : null}
                        {entry.resources.length > 0 ? (
                          <div className="space-y-4">
                            {entry.resources.map((resource) => {
                              const post = resourcePosts.get(resource.wpPostId);
                              if (post) {
                                return (
                                  <ResourceCard
                                    key={resource.id}
                                    post={post}
                                    view="list"
                                    fromHubSlug={hub.slug}
                                    attachments={attachmentsByPostId.get(post.id) ?? []}
                                    room={room}
                                  />
                                );
                              }
                              if (postsReady) return null;
                              return <ResourceCardSkeleton key={resource.id} />;
                            })}
                          </div>
                        ) : !hasChildren && entry.section?.type !== 'CONTENT' ? (
                          <p className="text-gray-500">No resources in this section yet.</p>
                        ) : null}
                      </>
                    );
                  }}
                />
                {normalizedQuery &&
                hubOutlineTree(visibleOutline).every((node) => node.entry?.resources.length === 0 && node.children.length === 0) ? (
                  <p className="text-gray-600">No resources match that title.</p>
                ) : null}
              </>
            ) : flatResources.length > 0 ? (
              <div id={sections.length === 1 ? anchors.get(sections[0].id) : undefined} className="space-y-4 scroll-mt-24">
                {sections.length === 1 && sections[0].description ? (
                  <p className="text-gray-600 mb-5 max-w-3xl">{sections[0].description}</p>
                ) : null}
                {(normalizedQuery ? visibleFlat : flatResources).map((resource) => {
                  const post = resourcePosts.get(resource.wpPostId);
                  if (post) {
                    return (
                      <ResourceCard
                        key={resource.id}
                        post={post}
                        view="list"
                        fromHubSlug={hub.slug}
                        attachments={attachmentsByPostId.get(post.id) ?? []}
                        room={room}
                      />
                    );
                  }
                  if (normalizedQuery || postsReady) return null;
                  return <ResourceCardSkeleton key={resource.id} />;
                })}
                {normalizedQuery && visibleFlat.length === 0 ? (
                  <p className="text-gray-600">No resources match that title.</p>
                ) : null}
              </div>
            ) : normalizedQuery ? (
              <p className="text-gray-600">No resources match that title.</p>
            ) : null}
            {flatResources.length === 0 && sections.length === 0 && !showLegacyWpBody && (
              <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-lg">
                <p className="mb-2 font-medium text-gray-700">No content in this toolkit yet</p>
                <p className="text-sm">
                  Sections and resources will appear here once they are added.
                </p>
              </div>
            )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}
